/**
 * Customer 360 — the world-class customer detail page.
 *
 * Architecture:
 *   1. Single composite GET /api/customers/:id/360 fetches everything for the page.
 *   2. Hero panel pins identity + lifecycle + LTV + churn risk at the top.
 *   3. Eight-KPI stats strip.
 *   4. Ten-tab navigation; mobile collapses to a dropdown.
 *   5. Each tab consumes the composite payload (no per-tab fetches).
 *
 * Backwards-compat: keeps the existing CustomerDetail route + named export.
 */
import { useAuthContext } from '@/auth/useAuthContext';
import { IssueCreditDrawer } from '@/components/IssueCreditDrawer';
import { SendWaiverLinkDrawer } from '@/components/SendWaiverLinkDrawer';
import {
  ApiError,
  type Customer360Response,
  computeCustomerChurnScore,
  getCustomer360,
  listCustomerCredits,
  voidCustomerCredit,
} from '@/lib/api';
import { formatPriceMinor } from '@/lib/format';
import type { AccountCredit } from '@/lib/types';
import { useRealtime } from '@/lib/useRealtime';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Customer360Hero } from './customer360/Customer360Hero';
import { Customer360Stats } from './customer360/Customer360Stats';
import { Customer360Tabs, type CustomerTab } from './customer360/Customer360Tabs';
import { HouseholdSection } from './customer360/HouseholdSection';
import { OverviewTab } from './customer360/OverviewTab';
import {
  AuditTab,
  BookingsTab,
  LoyaltyTab,
  MessagesTab,
  NotesTab,
  PaymentsTab,
  PetsTab,
  PhotosTab,
} from './customer360/tabs';

export function CustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { state } = useAuthContext();
  const currency = state.me?.activeAccount?.currency ?? 'GBP';
  const capabilities = state.me?.capabilities ?? [];
  const canViewChurn =
    capabilities.includes('customers.churn_score.view') || capabilities.includes('*');

  const [data, setData] = useState<Customer360Response | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creditOpen, setCreditOpen] = useState(false);
  const [waiverLinkOpen, setWaiverLinkOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<CustomerTab>('overview');
  const [computingChurn, setComputingChurn] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    setNotFound(false);
    try {
      const res = await getCustomer360(id);
      setData(res);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) setNotFound(true);
      else setError((e as Error).message ?? 'Failed to load customer');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  useRealtime(
    useCallback(
      (event) => {
        if (event.type === 'BOOKING_UPDATED') void load();
      },
      [load],
    ),
  );

  const handleComputeChurn = useCallback(async () => {
    if (!id) return;
    setComputingChurn(true);
    try {
      const r = await computeCustomerChurnScore(id);
      setData((prev) =>
        prev
          ? {
              ...prev,
              churnScore: {
                score: r.score,
                signals: r.signals,
                suggestedAction: r.suggestedAction,
                cachedAt: new Date().toISOString(),
              },
            }
          : prev,
      );
    } catch (e) {
      const code = e instanceof ApiError ? e.code : 'error';
      if (code !== 'ai_not_configured') {
        // surface gentle inline error; keep page usable
        // eslint-disable-next-line no-console
        console.warn('Churn score failed', code);
      }
    } finally {
      setComputingChurn(false);
    }
  }, [id]);

  const tabDefs = useMemo(() => {
    if (!data) return [];
    return [
      { id: 'overview' as CustomerTab, label: 'Overview' },
      { id: 'pets' as CustomerTab, label: 'Pets', count: data.pets.length },
      { id: 'bookings' as CustomerTab, label: 'Bookings', count: data.bookingsTotal },
      { id: 'photos' as CustomerTab, label: 'Photos', count: data.photos.length },
      { id: 'messages' as CustomerTab, label: 'Messages', count: data.messages.length },
      { id: 'payments' as CustomerTab, label: 'Payments', count: data.payments.length },
      { id: 'loyalty' as CustomerTab, label: 'Loyalty', count: data.loyalty.balance },

      { id: 'notes' as CustomerTab, label: 'Notes', count: data.notes.length },
      {
        id: 'audit' as CustomerTab,
        label: 'Audit',
        count: data.activityTimeline.length,
        hidden:
          !capabilities.includes('audit.read') &&
          !capabilities.includes('*') &&
          data.activityTimeline.length === 0,
      },
    ];
  }, [data, capabilities]);

  if (loading) {
    return (
      <div className="mx-auto max-w-[1400px] px-4 py-6 md:px-8">
        <SkeletonHero />
        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-8">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="h-14 rounded-xl border border-border bg-surface/40 animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  }
  if (notFound) {
    return (
      <div className="mx-auto max-w-[1400px] px-6 py-10 md:px-10">
        <Link
          to="/customers"
          className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground press"
        >
          <ArrowLeft className="size-4" strokeWidth={1.75} />
          Customers
        </Link>
        <div className="rounded-xl border border-border bg-surface px-6 py-12 text-center">
          <p className="mb-1 font-medium">Customer not found</p>
          <button
            type="button"
            onClick={() => navigate('/customers')}
            className="mt-3 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 press"
          >
            Back to customers
          </button>
        </div>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="mx-auto max-w-[1400px] px-6 py-10 md:px-10">
        <Link
          to="/customers"
          className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground press"
        >
          <ArrowLeft className="size-4" strokeWidth={1.75} />
          Customers
        </Link>
        <div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error ?? 'Failed to load customer'}
        </div>
      </div>
    );
  }

  const customerId = (data.customer as { id: string }).id;
  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6 md:px-8">
      <Link
        to="/customers"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground press"
      >
        <ArrowLeft className="size-4" strokeWidth={1.75} />
        Customers
      </Link>

      <Customer360Hero
        data={data}
        currency={currency}
        onEdit={() => navigate(`/customers/${customerId}/edit`)}
        onIssueCredit={() => setCreditOpen(true)}
        onAddNote={() => navigate(`/customers/${customerId}/edit?focus=notes`)}
        onSendWaiverLink={() => setWaiverLinkOpen(true)}
        onComputeChurn={handleComputeChurn}
        canViewChurn={canViewChurn}
        computingChurn={computingChurn}
      />

      <div className="mt-4">
        <Customer360Stats data={data} currency={currency} />
      </div>

      <div className="mt-6">
        <Customer360Tabs active={activeTab} tabs={tabDefs} onChange={setActiveTab} />
      </div>

      <div className="mt-5">
        {activeTab === 'overview' && <OverviewTab data={data} currency={currency} />}
        {activeTab === 'overview' && (
          <HouseholdSection
            customerId={customerId}
            canEdit={capabilities.includes('customers.edit') || capabilities.includes('*')}
          />
        )}
        {activeTab === 'pets' && <PetsTab data={data} currency={currency} />}
        {activeTab === 'bookings' && <BookingsTab data={data} currency={currency} />}
        {activeTab === 'photos' && <PhotosTab data={data} currency={currency} />}
        {activeTab === 'messages' && <MessagesTab data={data} currency={currency} />}
        {activeTab === 'payments' && (
          <>
            <PaymentsTab data={data} currency={currency} />
            <CreditsSection
              customerId={customerId}
              currency={currency}
              onIssueCredit={() => setCreditOpen(true)}
            />
          </>
        )}
        {activeTab === 'loyalty' && <LoyaltyTab data={data} currency={currency} />}
        {activeTab === 'notes' && <NotesTab data={data} currency={currency} />}
        {activeTab === 'audit' && <AuditTab data={data} currency={currency} />}
      </div>

      <IssueCreditDrawer
        customerId={customerId}
        open={creditOpen}
        onClose={() => setCreditOpen(false)}
        onIssued={() => void load()}
      />
      <SendWaiverLinkDrawer
        customerId={customerId}
        customerFirstName={data.customer.firstName}
        hasEmail={typeof data.customer.email === 'string' && data.customer.email.length > 0}
        hasPhone={typeof data.customer.phone === 'string' && data.customer.phone.length > 0}
        open={waiverLinkOpen}
        onClose={() => setWaiverLinkOpen(false)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// CreditsSection — fetches all credits for a customer + allows void
// ---------------------------------------------------------------------------

function CreditsSection({
  customerId,
  currency,
  onIssueCredit,
}: {
  customerId: string;
  currency: string;
  onIssueCredit: () => void;
}) {
  const { state } = useAuthContext();
  const capabilities = state.me?.capabilities ?? [];
  const canIssue = capabilities.includes('customers.credit_issue') || capabilities.includes('*');

  const [credits, setCredits] = useState<AccountCredit[]>([]);
  const [loading, setLoading] = useState(true);
  const [voidingId, setVoidingId] = useState<string | null>(null);

  const loadCredits = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listCustomerCredits(customerId);
      setCredits(res.credits);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    void loadCredits();
  }, [loadCredits]);

  async function handleVoid(credit: AccountCredit) {
    if (
      !window.confirm(
        `Void this credit (${formatPriceMinor(credit.amountMinor, currency)} — "${credit.reason}")? This cannot be undone.`,
      )
    )
      return;
    setVoidingId(credit.id);
    try {
      await voidCustomerCredit(customerId, credit.id);
      await loadCredits();
    } finally {
      setVoidingId(null);
    }
  }

  const statusBadge = (s: string) => {
    const base = 'inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium';
    if (s === 'active') return `${base} bg-emerald-100 text-emerald-800`;
    if (s === 'voided') return `${base} bg-rose-100 text-rose-700`;
    return `${base} bg-secondary text-muted-foreground`;
  };

  return (
    <div className="rounded-xl border border-border bg-surface mt-5">
      <div className="flex items-center justify-between border-b border-border px-5 py-3.5">
        <h2 className="text-sm font-semibold">Account credits</h2>
        {canIssue && (
          <button
            type="button"
            onClick={onIssueCredit}
            className="text-xs text-primary hover:underline press"
          >
            + Issue credit
          </button>
        )}
      </div>
      {loading ? (
        <div className="px-5 py-4 text-sm text-muted-foreground animate-pulse">Loading…</div>
      ) : credits.length === 0 ? (
        <div className="px-5 py-6 text-center text-sm text-muted-foreground">
          No credits on file.
        </div>
      ) : (
        <div className="divide-y divide-border">
          {credits.map((c) => (
            <div key={c.id} className="flex items-center gap-3 px-5 py-2.5 text-sm">
              <span className="num text-xs text-muted-foreground w-24 shrink-0">
                {c.createdAt.slice(0, 10)}
              </span>
              <span className="flex-1 truncate">{c.reason}</span>
              <span className="num text-xs font-medium text-foreground">
                {formatPriceMinor(c.remainingMinor, currency)}
                {c.remainingMinor !== c.amountMinor && (
                  <span className="ml-1 text-muted-foreground">
                    / {formatPriceMinor(c.amountMinor, currency)}
                  </span>
                )}
              </span>
              <span className={statusBadge(c.status)}>{c.status}</span>
              {c.expiresAt && (
                <span className="num text-xs text-muted-foreground">
                  exp {c.expiresAt.slice(0, 10)}
                </span>
              )}
              {canIssue && c.status === 'active' && (
                <button
                  type="button"
                  disabled={voidingId === c.id}
                  onClick={() => void handleVoid(c)}
                  title="Void credit"
                  className="ml-1 text-rose-500 hover:text-rose-700 disabled:opacity-40 press"
                >
                  <Trash2 className="size-3.5" strokeWidth={1.75} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SkeletonHero() {
  return (
    <div className="animate-pulse rounded-2xl border border-border bg-surface px-5 py-5">
      <div className="flex gap-4">
        <div className="size-20 rounded-full bg-secondary" />
        <div className="flex-1 space-y-2">
          <div className="h-5 w-1/3 rounded bg-secondary" />
          <div className="h-4 w-1/4 rounded bg-secondary/60" />
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
            <div className="h-12 rounded-lg bg-secondary/60" />
            <div className="h-12 rounded-lg bg-secondary/60" />
            <div className="h-12 rounded-lg bg-secondary/60" />
          </div>
        </div>
      </div>
    </div>
  );
}
